/**
 * RegisterPaymentPage (billing) — Registrar Pagamento (POST /billing/payments).
 *
 * 3 etapas:
 *  1) Dados do Recebimento — tudo do payload fora de invoices[]: data (+hora opcional), valor,
 *     tipo de pagamento e campos dependentes, fonte/sub-rogação/aval a devolver, pagador com
 *     busca no person service e comprovante [placeholder]. O responsável não é campo de tela:
 *     é o usuário logado. Os blocos de tipo de pagamento e de pagador são compartilhados com a
 *     baixa/liquidação de título (components/molecules/payment).
 *  2) Selecionar Contratos — busca no contracts-management + seleção de operações.
 *  3) Selecionar Faturas — invoices OPEN das operações; por fatura: value/total_payment/
 *     cancel_invoice/uncollect. Barra de alocação até 100% do valor informado (submete só em 100%).
 *
 * TODO(comprovante): upload de comprovante ainda sem backend (file_name_receipt/file_url_receipt).
 */
import { useCallback, useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    FaFileInvoiceDollar, FaChevronDown, FaChevronUp, FaCheckSquare, FaRegSquare,
    FaCloudUploadAlt, FaTimes,
} from 'react-icons/fa';
import { Auth } from 'aws-amplify';
import PaymentTypeFields, { PaymentTypeValue, emptyPaymentTypeValue, assetDationTotal } from '../../molecules/payment/PaymentTypeFields';
import PayerFields, { PayerValue, emptyPayer } from '../../molecules/payment/PayerFields';
import PaymentDateInput, { toIsoInstant } from '../../molecules/payment/PaymentDateInput';
import { amountHint } from '../../molecules/payment/paymentHints';
import HintBadge from '../../molecules/payment/HintBadge';
import { PaymentType, PaymentMethod } from '../../../constants/payment';
import { AuthContext } from '../../../services/Auth/AuthContext';
import { listInvoicesByOperation, processPayment, PaymentInvoiceItem } from './services/billingApi';
import { contractsManagementApi, OperationResult, PAYABLE_OPERATION_FILTERS } from '../../../services/contractsManagementApi';
import { FilterBuilder, AppliedFilter } from '../../molecules/FilterBuilder';
import { operationsFilterSchema } from '../operations/constants/operationFilterSchema';
import appConfig from '../../../config/app-config';
import { formatTaxId } from '../../../utils/taxIdUtils';
import {
    LiquidationMode, MODE_TO_TOTAL_PAYMENT, MODE_LABELS, PAYMENT_MODES_BY_SCENARIO,
    modeOf, clampMode,
} from './constants/liquidationModes';
import { CHARGE_SCENARIO_LABELS } from './constants/chargeFilterSchema';

const STEPS = ['Dados do Recebimento', 'Selecionar Contratos', 'Selecionar Faturas'] as const;

const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const onlyDigits = (s: string) => (s || '').replace(/\D/g, '');
// "2.500.000,00" | "2500000.00" → number
const parseBRL = (s: string) => {
    const cleaned = (s || '').trim();
    if (!cleaned) return 0;
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
};
const round2 = (n: number) => Math.round(n * 100) / 100;

// Valores iniciais do formulário — usados no useState E no reset pós-submit, para os
// dois nunca divergirem (era por isso que a tela voltava com dados da etapa 1).
/**
 * Chave de idempotência do pagamento (opcional no payload). `crypto.randomUUID` só
 * existe em contexto seguro, e o próprio global `crypto` pode não existir — sem esta
 * guarda o envio estourava "crypto is not defined" antes de chamar a API.
 */
const newPaymentId = (): string | undefined =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : undefined;

const today = () => new Date().toISOString().slice(0, 10);
const INITIAL_TIME = '00:00';
const INITIAL_INV_SORT = 'dueDate' as const;

/** Valor esperado da fatura para as dicas de desconto/acréscimo (o que falta receber). */
const invoiceExpected = (inv: Invoice) => round2(inv.amount - (inv.amountPaid || 0));

interface Invoice {
    id: number;
    invoiceId: string;
    chargeId: string | null;
    creditOperationId: string;
    amount: number;
    amountPaid: number | null;
    dueDate: string;
    status: string;
    statusFormatted?: string;
    documentTitle?: string | null;
    installmentNumber?: number | null;
    payer?: { name: string; documentNumber: string | null };
    /** cenário do charge — decide quais modos de liquidação a tela pode oferecer nesta fatura */
    chargeScenario?: string;
    allowsPartialPayment?: boolean;
    /** modo que a própria fatura declarava (tri-state; null = baldinho) */
    totalPayment?: boolean | null;
    _op?: string; // nº do contrato (para exibição)
}

interface ItemEdit {
    value: string;          // BRL string editável
    /** TOTAL | PARCIAL | OPERATION_WATERFALL — convertido em total_payment tri-state no submit */
    mode: LiquidationMode;
    cancel_invoice: boolean;
    uncollect: boolean;
}

/**
 * Modos válidos para esta fatura. Sem `chargeScenario` (API antiga) cai em GUARANTEE, que só
 * aceita total — o mesmo que a tela fazia antes deste campo existir.
 */
const modesOf = (inv: Invoice): LiquidationMode[] =>
    PAYMENT_MODES_BY_SCENARIO[inv.chargeScenario || 'GUARANTEE'] || ['TOTAL'];

const RegisterPaymentPage = () => {
    const [step, setStep] = useState(0);

    // ── Step 1 ──
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(today);
    const [time, setTime] = useState(INITIAL_TIME);
    const [showTime, setShowTime] = useState(false);
    const [typeFields, setTypeFields] = useState<PaymentTypeValue>(emptyPaymentTypeValue());
    const [notes, setNotes] = useState('');
    const [receiptName, setReceiptName] = useState('');
    const [payer, setPayer] = useState<PayerValue>(() => emptyPayer());
    // "Responsável" não é mais campo de tela: é quem está logado realizando a ação
    const auth = useContext(AuthContext);
    const userResponsible = auth?.user?.username || auth?.user?.email || '';

    // ── Step 2 ──
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<OperationResult[]>([]);
    const [opFilters, setOpFilters] = useState<AppliedFilter[]>([]);
    const [opPage, setOpPage] = useState(0);
    const [opTotal, setOpTotal] = useState(0);
    const [opSearched, setOpSearched] = useState(false);
    const [selectedOps, setSelectedOps] = useState<OperationResult[]>([]);

    // ── Step 3 ──
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [edits, setEdits] = useState<Record<string, ItemEdit>>({}); // key = invoiceId (selecionados)
    const [invSearch, setInvSearch] = useState('');
    const [invSort, setInvSort] = useState<'dueDate' | 'amount' | 'payer'>(INITIAL_INV_SORT);
    const [submitting, setSubmitting] = useState(false);
    // resumo da operação (contrato/aditamento/valor/pago) p/ cabeçalho dos grupos — via settlement /operations
    const [opSummaries, setOpSummaries] = useState<Record<string, { label: string; amount: number; paid: number }>>({});
    const [openSel, setOpenSel] = useState(true);   // accordion: faturas selecionadas
    const [openList, setOpenList] = useState(true);  // accordion: listagem/seleção

    const paymentAmount = round2(parseBRL(amount));

    // conta de recebimento só é exigida em pagamento financeiro
    const step1Valid = paymentAmount > 0 && !!date
        && (typeFields.payment_type !== PaymentType.FINANCIAL || !!typeFields.receiving_bank_account)
        && onlyDigits(payer.document).length >= 11;

    // ─── Step 2: busca de operações (contracts-management) ───────────────────
    // GET /contracts/receivables_assignment/list (view unificada credit.vw_list_operations),
    // com status/billed chumbados — sem faturamento não existe fatura para receber.
    const OP_PAGE_SIZE = 20;
    const fetchOperations = useCallback(async (filters: AppliedFilter[], page: number) => {
        setSearching(true);
        try {
            const data = await contractsManagementApi.listOperations({
                filters, page, pageSize: OP_PAGE_SIZE,
                fixedFilters: PAYABLE_OPERATION_FILTERS,
            });
            setResults(data.list);
            setOpTotal(data.totalElements);
            setOpSearched(true);
        } catch (e: any) {
            toast.error(`Erro ao buscar operações: ${e.message}`);
        } finally {
            setSearching(false);
        }
    }, []);

    const runSearch = useCallback((filters: AppliedFilter[]) => {
        setOpFilters(filters);
        setOpPage(0);
        fetchOperations(filters, 0);
    }, [fetchOperations]);

    const changeOpPage = (page: number) => {
        setOpPage(page);
        fetchOperations(opFilters, page);
    };

    const opLabel = (op: OperationResult) => {
        const contract = op.sequencial_number ?? '—';
        return op.amendment_number && op.amendment_number > 0 ? `${contract}-${op.amendment_number}` : `${contract}`;
    };
    const opParty = (op: OperationResult) =>
        (op.participants || []).find((p: any) => ['ASSIGNOR', 'BORROWER'].includes(p.participant_type_name))
        || (op.participants || [])[0];

    const toggleOp = (op: OperationResult) => {
        setSelectedOps(prev => prev.find(o => o.credit_operation_id === op.credit_operation_id)
            ? prev.filter(o => o.credit_operation_id !== op.credit_operation_id)
            : [...prev, op]);
    };
    const isOpSelected = (op: OperationResult) => !!selectedOps.find(o => o.credit_operation_id === op.credit_operation_id);

    // resumo das operações (contrato-aditamento | valor | pago) — reusa settlement /operations.
    // TODO: idealmente um endpoint/view próprio do billing para esta tela.
    const fetchOpSummaries = async (ops: OperationResult[]) => {
        try {
            const { address, key } = appConfig.apis.settlementV2;
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const map: Record<string, { label: string; amount: number; paid: number }> = {};
            await Promise.all(ops.map(async op => {
                try {
                    // o endpoint filtra por filter[credit_operation_id] (padrão FilterBuilder), não credit_operation_id puro
                    const qs = new URLSearchParams({ 'filter[credit_operation_id]': op.credit_operation_id, size: '1' });
                    const res = await fetch(`${address}/operations?${qs.toString()}`,
                        { headers: { 'x-api-key': key, Authorization: `Bearer ${token}` } });
                    if (!res.ok) return;
                    const o = ((await res.json()).list || [])[0];
                    if (o) map[op.credit_operation_id] = {
                        label: `${o.contract_number ?? '—'}${o.amendment_number != null ? '-' + o.amendment_number : ''}`,
                        amount: Number(o.amount || 0), paid: Number(o.paid_amount || 0),
                    };
                } catch { /* operação sem resumo no settlement */ }
            }));
            setOpSummaries(map);
        } catch { /* sem sessão/settlement — cabeçalho usa fallback */ }
    };

    // ─── Step 3: carregar invoices OPEN das operações selecionadas ───────────
    const goToInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const all: Invoice[] = [];
            for (const op of selectedOps) {
                const data = await listInvoicesByOperation(op.credit_operation_id);
                (data.list || [])
                    .filter((inv: Invoice) => inv.status === 'OPEN')
                    .forEach((inv: Invoice) => all.push({ ...inv, _op: op.operation_number }));
            }
            setInvoices(all);
            await fetchOpSummaries(selectedOps);
            setStep(2);
        } catch (e: any) {
            toast.error(`Erro ao carregar faturas: ${e.message}`);
        } finally {
            setLoadingInvoices(false);
        }
    };

    // o default é o modo que a PRÓPRIA fatura declarava, limitado ao que o cenário aceita:
    // uma parcela paga por garantia nasce baldinho, e forçar TOTAL aqui liquidaria o título
    const defaultEdit = (inv: Invoice): ItemEdit => ({
        value: fmt(inv.amount),
        mode: clampMode(modeOf(inv.totalPayment), modesOf(inv)),
        cancel_invoice: true,
        uncollect: true,
    });

    const toggleInvoice = (inv: Invoice) => {
        setEdits(prev => {
            const next = { ...prev };
            if (next[inv.invoiceId]) delete next[inv.invoiceId];
            else next[inv.invoiceId] = defaultEdit(inv);
            return next;
        });
    };

    const patchEdit = (inv: Invoice, patch: Partial<ItemEdit>) => {
        setEdits(prev => {
            const cur = prev[inv.invoiceId];
            if (!cur) return prev;
            let next = { ...cur, ...patch };
            // value alterado → ajusta o modo automaticamente (usuário ainda pode remarcar).
            // Duas exceções: OPERATION_WATERFALL é uma DECLARAÇÃO da fatura (o dinheiro entra na operação,
            // não no título) e não se troca por comparação de valor; e cenário que não aceita
            // PARCIAL (garantia) não pode ser rebaixado.
            const allowedModes = modesOf(inv);
            if (patch.value !== undefined && cur.mode !== 'OPERATION_WATERFALL') {
                const v = round2(parseBRL(patch.value));
                const target: LiquidationMode = v < inv.amount ? 'PARTIAL' : 'TOTAL';
                if (allowedModes.includes(target)) next.mode = target;
            }
            // cancel_invoice=true força uncollect=true (e trava); false libera uncollect
            if (patch.cancel_invoice === true) next.uncollect = true;
            return { ...prev, [inv.invoiceId]: next };
        });
    };

    const isSelected = (inv: Invoice) => !!edits[inv.invoiceId];

    const filteredInvoices = useMemo(() => {
        const q = invSearch.trim().toLowerCase();
        let list = invoices;
        if (q) {
            list = list.filter(inv =>
                (inv.payer?.name || '').toLowerCase().includes(q) ||
                (inv.chargeId || '').toLowerCase().includes(q) ||
                (inv.documentTitle || '').toLowerCase().includes(q) ||
                (inv._op || '').toLowerCase().includes(q));
        }
        const sorted = [...list].sort((a, b) => {
            if (invSort === 'amount') return b.amount - a.amount;
            if (invSort === 'payer') return (a.payer?.name || '').localeCompare(b.payer?.name || '');
            return (a.dueDate || '').localeCompare(b.dueDate || '');
        });
        return sorted;
    }, [invoices, invSearch, invSort]);

    // agrupa os invoices filtrados por operação, preservando a ordem das operações selecionadas
    const groups = useMemo(() => {
        const byOp: Record<string, Invoice[]> = {};
        for (const inv of filteredInvoices) (byOp[inv.creditOperationId] ||= []).push(inv);
        return selectedOps
            .map(op => ({ op, list: byOp[op.credit_operation_id] || [] }))
            .filter(g => g.list.length > 0);
    }, [filteredInvoices, selectedOps]);

    // invoices selecionados (na ordem em que foram carregados)
    const selectedInvoices = useMemo(() => invoices.filter(inv => edits[inv.invoiceId]), [invoices, edits]);

    const allocated = useMemo(() =>
        round2(Object.values(edits).reduce((s, e) => s + parseBRL(e.value), 0)), [edits]);
    const pct = paymentAmount > 0 ? Math.min(100, (allocated / paymentAmount) * 100) : 0;
    const over = allocated > paymentAmount;
    const canSubmit = paymentAmount > 0 && round2(allocated) === paymentAmount && Object.keys(edits).length > 0;

    // ─── Submit ──────────────────────────────────────────────────────────────
    const buildPayload = () => {
        const payment_date = toIsoInstant(date, time, showTime)!; // wall-clock local → UTC (sem D±1)
        const doc = onlyDigits(payer.document);
        const items: PaymentInvoiceItem[] = invoices
            .filter(inv => edits[inv.invoiceId])
            .map(inv => {
                const e = edits[inv.invoiceId];
                return {
                    credit_operation_id: inv.creditOperationId,
                    charge_id: inv.chargeId,
                    invoice_id: inv.invoiceId,
                    value: round2(parseBRL(e.value)),
                    total_payment: MODE_TO_TOTAL_PAYMENT[e.mode],
                    cancel_invoice: e.cancel_invoice,
                    uncollect: e.uncollect,
                };
            });
        return {
            payment_id: newPaymentId(),
            amount: paymentAmount,
            payment_date,
            // meio de pagamento e conta só existem em pagamento financeiro
            payment_method: typeFields.payment_type === PaymentType.FINANCIAL
                ? typeFields.payment_method : undefined,
            payment_type: typeFields.payment_type,
            receiving_bank_account_id: typeFields.payment_type === PaymentType.FINANCIAL
                ? typeFields.receiving_bank_account : undefined,
            user_responsible: userResponsible || undefined,
            file_name_receipt: receiptName || undefined,
            payment_source: typeFields.payment_source,
            is_subrogation: typeFields.is_subrogation,
            pending_guarantor_refund: typeFields.pending_guarantor_refund,
            debt_confession_contract: typeFields.payment_type === PaymentType.DEBT_CONFESSION
                ? typeFields.debt_confession_contract || undefined : undefined,
            asset_dation_list: typeFields.payment_type === PaymentType.ASSET_DATION
                ? typeFields.asset_dation_list : undefined,
            data: notes ? { notes } : undefined,
            payer: {
                external_id: payer.personId || undefined,  // ausente → billing cria person sem external_id
                name: payer.name || doc,                   // name é NOT NULL no billing; fallback documento
                document_number: doc,
                email: payer.email || undefined,
                phone: payer.phone || undefined,
                address_street: payer.address_street || undefined,
                address_number: payer.address_number || undefined,
                address_complement: payer.address_complement || undefined,
                address_neighborhood: payer.address_neighborhood || undefined,
                address_city: payer.address_city || undefined,
                address_state: payer.address_state || undefined,
                address_zip_code: payer.address_zip_code || undefined,
            },
            invoices: items,
        };
    };

    /** Volta a tela inteira (as 3 etapas) ao estado inicial. */
    const resetForm = () => {
        setStep(0);
        // etapa 1
        setAmount(''); setDate(today()); setTime(INITIAL_TIME); setShowTime(false);
        setTypeFields(emptyPaymentTypeValue()); setNotes(''); setReceiptName('');
        setPayer(emptyPayer());
        // etapa 2
        setResults([]); setOpFilters([]); setOpPage(0); setOpTotal(0);
        setOpSearched(false); setSelectedOps([]);
        // etapa 3
        setInvoices([]); setEdits({}); setInvSearch(''); setInvSort(INITIAL_INV_SORT);
        setOpSummaries({}); setOpenSel(true); setOpenList(true);
        // PayerFields, FilterBuilder e o select de conta têm estado interno próprio, mas
        // desmontam ao trocar de etapa — voltar para a etapa 1 já os remonta limpos.
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await processPayment(buildPayload());
            const n = res?.data?.settled?.length ?? 0;
            toast.success(`Pagamento registrado — ${n} fatura(s) processada(s)`);
            resetForm();
        } catch (e: any) {
            toast.error(`Erro ao registrar pagamento: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const input = 'w-full border rounded px-3 py-2 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-slate-800 disabled:cursor-not-allowed';
    const label = 'text-xs text-gray-500 dark:text-gray-400 block mb-1';

    const fmtDate = (d?: string) => (d ? d.split('-').reverse().join('/') : '—');

    // cabeçalho enriquecido do invoice (documento · pagador … valor / Fatura · op · Recebível · vence)
    const invHeader = (inv: Invoice) => (
        <div className="flex-1 min-w-0">
            <div className="flex justify-between gap-2">
                <span className="text-sm font-medium truncate">
                    {inv.payer?.documentNumber ? `${formatTaxId(inv.payer.documentNumber)} · ` : ''}{inv.payer?.name || '—'}
                </span>
                <span className="text-sm font-medium whitespace-nowrap">R$ {fmt(inv.amount)}</span>
            </div>
            <div className="flex justify-between gap-2 text-xs text-gray-500">
                <span className="truncate">
                    Fatura {inv.invoiceId.slice(0, 8)} · op. {inv.creditOperationId.slice(0, 8)}
                    {inv.chargeId ? ` · Recebível ${inv.chargeId.slice(0, 8)}` : ''}
                    {/* natureza à vista: é ela que explica por que os modos ofertados mudam */}
                    {inv.chargeScenario && (
                        <> · {CHARGE_SCENARIO_LABELS[inv.chargeScenario] || inv.chargeScenario}</>
                    )}
                </span>
                <span className="whitespace-nowrap">vence {fmtDate(inv.dueDate)}</span>
            </div>
        </div>
    );

    /**
     * Efeito no título. O widget muda com o cenário porque as escolhas mudam:
     *   RECEIVABLE  → checkbox liquida/não liquida (como sempre foi)
     *   INSTALLMENT → select com os três, incl. baldinho (pago pela garantia)
     *   GUARANTEE   → travado em total, com o motivo à vista
     */
    const liquidationControl = (inv: Invoice, e: ItemEdit) => {
        const allowedModes = modesOf(inv);
        const cor = e.mode === 'TOTAL' ? 'bg-green-500' : 'bg-amber-500';

        if (allowedModes.length <= 1) {
            return (
                <div className="mt-5 self-start">
                    <span className="inline-block px-2 py-1 rounded text-sm bg-green-500 text-white">
                        {MODE_LABELS.TOTAL}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                        Recebível de garantia é sempre total — não aceita parcial.
                    </p>
                </div>
            );
        }
        if (allowedModes.includes('OPERATION_WATERFALL')) {
            return (
                <div className="mt-5 self-start w-full">
                    <label className={label} htmlFor={`modo-${inv.invoiceId}`}>Efeito no título</label>
                    <select id={`modo-${inv.invoiceId}`} className={`${input} ${cor} text-white border-0`}
                        value={e.mode}
                        onChange={ev => patchEdit(inv, { mode: ev.target.value as LiquidationMode })}>
                        {allowedModes.map(m => <option key={m} value={m}>{MODE_LABELS[m]}</option>)}
                    </select>
                    {e.mode === 'OPERATION_WATERFALL' && (
                        <p className="text-xs text-gray-500 mt-1">
                            O valor entra na operação e é distribuído pelas parcelas — não liquida
                            esta parcela diretamente.
                        </p>
                    )}
                </div>
            );
        }
        // estado destacado nas cores dos botões da tela de títulos:
        // verde = liquida (Liquidar), âmbar = não liquida (Baixa Parcial)
        return (
            <label className={`flex items-center gap-2 text-sm mt-5 px-2 py-1 rounded cursor-pointer transition-colors self-start ${e.mode === 'TOTAL'
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                }`}>
                <input type="checkbox" className="accent-white" checked={e.mode === 'TOTAL'}
                    onChange={ev => patchEdit(inv, { mode: ev.target.checked ? 'TOTAL' : 'PARTIAL' })} />
                Quitação total do título
            </label>
        );
    };

    const editControls = (inv: Invoice, e: ItemEdit) => (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2" onClick={ev => ev.stopPropagation()}>
            <div>
                <label className={label}>Valor pago (R$)</label>
                <input className={input} value={e.value} onChange={ev => patchEdit(inv, { value: ev.target.value })} />
                {/* desconto/acréscimo só quando quita o título (em pagamento parcial o valor
                    menor é expected, não é desconto) */}
                <HintBadge hint={e.mode === 'TOTAL' ? amountHint(parseBRL(e.value), invoiceExpected(inv)) : null} />
            </div>
            {liquidationControl(inv, e)}
            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={e.cancel_invoice} onChange={ev => patchEdit(inv, { cancel_invoice: ev.target.checked })} />
                Cancelar fatura original
            </label>
            <label className={`flex items-center gap-2 text-sm ${e.cancel_invoice ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={e.uncollect} disabled={e.cancel_invoice} onChange={ev => patchEdit(inv, { uncollect: ev.target.checked })} />
                Parar de cobrar fatura original
            </label>
        </div>
    );

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                    <FaFileInvoiceDollar className="text-green-600" /> Registrar Pagamento
                </h1>
                <p className="text-sm text-gray-500 mt-1">Um pagamento liquida uma ou mais faturas (cobrança)</p>
            </div>

            {/* Stepper */}
            <div className="flex items-center mb-8 bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
                {STEPS.map((s, idx) => (
                    <div key={s} className="flex items-center flex-1">
                        <div className={`flex items-center gap-2 ${idx <= step ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${idx < step ? 'bg-green-500 text-white' : idx === step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                {idx < step ? '✓' : idx + 1}
                            </span>
                            <span className="text-sm">{s}</span>
                        </div>
                        {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-4 ${idx < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
                    </div>
                ))}
            </div>

            {/* ═══ STEP 1 ═══ */}
            {step === 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
                    <h2 className="font-semibold text-gray-800 dark:text-white mb-4">Dados do Recebimento</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={label}>Data do Pagamento *</label>
                            <PaymentDateInput date={date} time={time} showTime={showTime} className={input}
                                onChange={next => { setDate(next.date); setTime(next.time); setShowTime(next.showTime); }} />
                        </div>
                        <div>
                            <label className={label}>Valor Recebido (R$) *</label>
                            <input type="text" value={amount} onChange={e => setAmount(e.target.value)}
                                placeholder="2.500.000,00" className={`${input} text-lg font-semibold`} />
                        </div>
                    </div>

                    {/* Tipo de pagamento + campos dependentes (mesmo bloco da baixa de título) */}
                    <div className="mt-4">
                        <PaymentTypeFields
                            value={typeFields}
                            onChange={next => {
                                // dação: o valor recebido acompanha o total dos bens
                                const total = assetDationTotal(next.asset_dation_list);
                                if (next.payment_type === PaymentType.ASSET_DATION
                                    && total !== assetDationTotal(typeFields.asset_dation_list)) {
                                    setAmount(total.toFixed(2).replace('.', ','));
                                }
                                setTypeFields(next);
                            }}
                            methods={[PaymentMethod.PIX, PaymentMethod.TRANSFER, PaymentMethod.BOLETO]}
                            labelClassName={label}
                            inputClassName={input}
                            idPrefix="register-payment"
                        />
                    </div>

                    {/* Pagador (bloco compartilhado com a baixa de título) */}
                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <PayerFields value={payer} onChange={setPayer}
                            labelClassName={label} inputClassName={input} />
                    </div>

                    {/* Comprovante (placeholder — backend pendente) */}
                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <label className={label}>Comprovante de pagamento
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">TODO: backend de upload pendente</span>
                        </label>
                        <label className="flex items-center gap-2 border border-dashed dark:border-slate-600 rounded px-3 py-3 text-sm text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700">
                            <FaCloudUploadAlt /> {receiptName || 'Selecionar arquivo (não sentPayload ainda)'}
                            <input type="file" className="hidden" onChange={e => setReceiptName(e.target.files?.[0]?.name || '')} />
                        </label>
                    </div>

                    <div className="mt-4">
                        <label className={label}>Observações</label>
                        <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                            placeholder="Referência bancária, NF, etc." className={input} />
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button disabled={!step1Valid} onClick={() => setStep(1)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">Avançar →</button>
                    </div>
                </div>
            )}

            {/* ═══ STEP 2 ═══ */}
            {step === 1 && (
                <>
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="font-semibold text-gray-800 dark:text-white">Buscar Operações de Crédito</h2>
                        <div className="text-sm font-semibold text-green-700">Valor: R$ {fmt(paymentAmount)}</div>
                    </div>

                    <FilterBuilder
                        schema={operationsFilterSchema}
                        onSearch={runSearch}
                        loading={searching}
                        title="Filtros de operações"
                    />

                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                        {selectedOps.length > 0 && (
                            <div className="px-4 py-3 bg-blue-50 dark:bg-slate-700/40 border-b dark:border-slate-700">
                                <span className="text-xs font-semibold text-blue-700 uppercase">Selecionados:</span>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {selectedOps.map(o => (
                                        <span key={o.credit_operation_id} className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-slate-800 border border-blue-200 dark:border-slate-600 rounded text-sm">
                                            {opLabel(o)} <span className="text-gray-400 text-xs">{o.operation_number}</span>
                                            <button onClick={() => toggleOp(o)} className="text-gray-400 hover:text-red-500 ml-1"><FaTimes size={11} /></button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {results.length > 0 ? (
                            <>
                                <div className="divide-y dark:divide-slate-700 max-h-[50vh] overflow-y-auto">
                                    {results.map(op => {
                                        const party = opParty(op);
                                        return (
                                            <div key={op.credit_operation_id} className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/40 cursor-pointer"
                                                onClick={() => toggleOp(op)}>
                                                <span className="mr-3">{isOpSelected(op) ? <FaCheckSquare className="text-blue-600 text-lg" /> : <FaRegSquare className="text-lg text-gray-400" />}</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-baseline gap-2">
                                                        <span className="font-medium">{opLabel(op)}</span>
                                                        <span className="text-xs text-gray-500 truncate">{op.operation_number}</span>
                                                        <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-slate-700 rounded whitespace-nowrap">{op.operation_type}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        {party?.document ? `${formatTaxId(party.document)} · ` : ''}{party?.name || '—'}
                                                        {op.credit_product_type_name ? ` · ${op.credit_product_type_name}` : ''}
                                                    </div>
                                                </div>
                                                <div className="text-right whitespace-nowrap ml-2">
                                                    <div className="text-sm">R$ {fmt(Number(op.approved_value || 0))}</div>
                                                    {op.expiration_date && (
                                                        <div className="text-xs text-gray-500">vence {op.expiration_date.slice(0, 10).split('-').reverse().join('/')}</div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {opTotal > OP_PAGE_SIZE && (
                                    <div className="flex items-center justify-between px-4 py-2 border-t dark:border-slate-700 text-xs text-gray-500">
                                        <span>{opTotal} operação(ões) · página {opPage + 1} de {Math.ceil(opTotal / OP_PAGE_SIZE)}</span>
                                        <div className="flex gap-2">
                                            <button disabled={opPage === 0 || searching} onClick={() => changeOpPage(opPage - 1)}
                                                className="px-2 py-1 border rounded disabled:opacity-40 dark:border-slate-600">← Anterior</button>
                                            <button disabled={(opPage + 1) * OP_PAGE_SIZE >= opTotal || searching} onClick={() => changeOpPage(opPage + 1)}
                                                className="px-2 py-1 border rounded disabled:opacity-40 dark:border-slate-600">Próxima →</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="p-8 text-center text-gray-400">
                                {searching ? 'Buscando...' : opSearched ? 'Nenhuma operação encontrada com esses filtros' : 'Use os filtros acima para buscar operações'}
                            </div>
                        )}

                        <div className="p-4 border-t dark:border-slate-700 flex items-center justify-between bg-gray-50 dark:bg-slate-800">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{selectedOps.length} operação(ões) selecionada(s)</span>
                            <div className="flex gap-3">
                                <button onClick={() => setStep(0)} className="px-4 py-2 border rounded-lg dark:border-slate-600">← Voltar</button>
                                <button disabled={selectedOps.length === 0 || loadingInvoices} onClick={goToInvoices}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                                    {loadingInvoices ? 'Carregando...' : 'Avançar →'}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ═══ STEP 3 ═══ */}
            {step === 2 && (
                <div className="space-y-4">
                    {/* Barra de alocação (sempre visível) */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-600 dark:text-gray-300">Alocado: <b className={over ? 'text-red-600' : 'text-green-700'}>R$ {fmt(allocated)}</b> / R$ {fmt(paymentAmount)}</span>
                            <span className={`font-semibold ${over ? 'text-red-600' : round2(allocated) === paymentAmount ? 'text-green-600' : 'text-gray-500'}`}>
                                {over ? `Excede R$ ${fmt(allocated - paymentAmount)}` : round2(allocated) === paymentAmount ? '✓ 100%' : `Falta R$ ${fmt(paymentAmount - allocated)}`}
                            </span>
                        </div>
                        <div className="w-full h-3 bg-gray-200 dark:bg-slate-700 rounded overflow-hidden">
                            <div className={`h-full transition-all ${over ? 'bg-red-500' : pct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                    </div>

                    {/* ── Accordion A: Faturas selecionadas (editar + submit) ── */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                        <button onClick={() => setOpenSel(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800 dark:text-white bg-gray-300 dark:bg-slate-700 hover:bg-gray-300/80">
                            <span>Faturas selecionadas: {selectedInvoices.length}</span>
                            {openSel ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                        </button>
                        {openSel && (
                            <div className="border-t dark:border-slate-700 max-h-[45vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-3 space-y-2">
                                {selectedInvoices.length === 0 && (
                                    <div className="p-4 text-center text-gray-400 text-sm">Nenhuma fatura selecionada — selecione na área abaixo</div>
                                )}
                                {selectedInvoices.map(inv => {
                                    const e = edits[inv.invoiceId];
                                    return (
                                        <div key={inv.invoiceId} className="bg-[#f0f8ff] dark:bg-slate-700/40 rounded-md border border-blue-200 dark:border-slate-700 px-4 py-3">
                                            <div className="flex items-start gap-3">
                                                {invHeader(inv)}
                                                <button onClick={() => toggleInvoice(inv)} title="Remover da seleção"
                                                    className="text-gray-400 hover:text-red-500 mt-0.5"><FaTimes /></button>
                                            </div>
                                            {e && editControls(inv, e)}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* ── Accordion B: Operações / Faturas (listagem + seleção) ── */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                        <button onClick={() => setOpenList(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800 dark:text-white bg-gray-300 dark:bg-slate-700 hover:bg-gray-300/80">
                            <span>Operações: {selectedOps.length} · Faturas: {invoices.length}</span>
                            {openList ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                        </button>
                        {openList && (
                            <div className="border-t dark:border-slate-700">
                                <div className="p-3 border-b dark:border-slate-700 flex flex-col sm:flex-row gap-2 sm:items-center">
                                    <input value={invSearch} onChange={e => setInvSearch(e.target.value)}
                                        placeholder="Filtrar por pagador, recebível, contrato..." className={`${input} flex-1`} />
                                    <div className="flex items-center gap-2 whitespace-nowrap">
                                        <span className="text-xs text-gray-500">Ordenar:</span>
                                        <select value={invSort} onChange={e => setInvSort(e.target.value as any)} className={`${input} w-auto`}>
                                            <option value="dueDate">Vencimento</option>
                                            <option value="amount">Valor</option>
                                            <option value="payer">Pagador</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="max-h-[45vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-3 space-y-3">
                                    {groups.length === 0 && <div className="p-8 text-center text-gray-400">Nenhuma fatura OPEN encontrada</div>}
                                    {groups.map(({ op, list }) => {
                                        const s = opSummaries[op.credit_operation_id];
                                        return (
                                            <div key={op.credit_operation_id} className="rounded-md border border-gray-300 dark:border-slate-700 overflow-hidden">
                                                <div className="px-4 py-2 bg-gray-200 dark:bg-slate-700/50 text-sm font-medium flex flex-wrap gap-x-3 gap-y-1">
                                                    <span>{s?.label || op.operation_number}</span>
                                                    {s && <span className="text-gray-500 dark:text-gray-400 font-normal">| Valor: R$ {fmt(s.amount)}</span>}
                                                    {s && <span className="text-gray-500 dark:text-gray-400 font-normal">| Pago: R$ {fmt(s.paid)}</span>}
                                                    <span className="text-gray-500 dark:text-gray-400 font-normal">| Faturas: {list.length}</span>
                                                </div>
                                                <div className="p-2 space-y-1.5 bg-gray-100/70 dark:bg-slate-800/40">
                                                    {list.map(inv => {
                                                        const sel = isSelected(inv);
                                                        return (
                                                            <div key={inv.invoiceId}
                                                                className={`flex items-center gap-3 px-3 py-2 rounded border cursor-pointer ${sel ? 'border-blue-300 dark:border-blue-500 bg-[#f0f8ff] dark:bg-slate-700/50' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/30'}`}
                                                                onClick={() => toggleInvoice(inv)}>
                                                                <span>{sel ? <FaCheckSquare className="text-blue-600" /> : <FaRegSquare className="text-gray-300" />}</span>
                                                                {invHeader(inv)}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 flex items-center justify-between">
                        <button onClick={() => setStep(1)} className="px-4 py-2 border rounded-lg dark:border-slate-600">← Voltar</button>
                        <button disabled={!canSubmit || submitting} onClick={submit}
                            title={canSubmit ? '' : 'A soma dos valores deve ser exatamente 100% do valor recebido'}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50 hover:bg-green-700">
                            {submitting ? 'Registrando...' : 'Confirmar Pagamento'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegisterPaymentPage;
